import React, { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
  PieChart, Pie, CartesianGrid, LabelList
} from 'recharts';
import {
  Users, Tag, Folder, Clock, Shield, BarChart2,
  Ticket as TicketIcon, CheckCircle2, MessageSquare,
  ArrowUp, ArrowDown, ArrowRight, ChevronDown, Inbox,
  LayoutDashboard
} from 'lucide-react';
import { useTicketStore, Ticket } from '../../store/ticketStore';
import { useSupportSettingsStore } from '../../store/supportSettingsStore';
import { useRbacStore } from '../../store/rbacStore';
import { cn } from '../../lib/utils';

// Harmonious chart palettes matching the sample design
const TEAM_PALETTE = ['#2563EB', '#10B981', '#F97316', '#8B5CF6', '#EF4444', '#64748B'];
const CAT_PALETTE = ['#2563EB', '#10B981', '#F97316', '#8B5CF6', '#EF4444', '#64748B'];

const TYPE_PALETTE: Record<string, string> = {
  'Incident': '#2563EB',
  'Service Request': '#10B981',
  'Problem': '#F97316',
  'Change Request': '#8B5CF6',
  'Access Request': '#EF4444',
  'Other': '#64748B',
};

const formatTimeAgo = (dateStr?: string) => {
  if (!dateStr) return '—';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '—';
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    if (diffMs < 0) return 'Just now';
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);

    if (diffSec < 60) return 'Just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    if (diffHour < 24) return `${diffHour}h ago`;
    if (diffDay < 7) return `${diffDay}d ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return '—';
  }
};

const formatDuration = (ms: number): string => {
  if (!Number.isFinite(ms) || ms <= 0) return '—';
  const minutes = Math.round(ms / (1000 * 60));
  if (minutes < 60) {
    return `${Math.max(1, minutes)}m`;
  }
  const hours = ms / (1000 * 60 * 60);
  if (hours < 10) {
    return `${hours.toFixed(1)}h`;
  }
  return `${Math.round(hours)}h`;
};

// Render slice percentage labels inside pie chart
const RADIAN = Math.PI / 180;
const renderCustomizedPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }: any) => {
  const normalized = typeof percent === 'number' && percent > 1 ? percent / 100 : Number(percent || 0);
  if (!normalized || normalized <= 0.04) return null;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.55;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill="white"
      textAnchor="middle"
      dominantBaseline="central"
      fontSize={10.5}
      fontWeight={600}
    >
      {`${Math.round(normalized * 100)}%`}
    </text>
  );
};

export function SupportDashboard() {
  const navigate = useNavigate();
  const { dashboardTickets, tickets: storeTickets, error, fetchDashboardTickets, fetchTickets } = useTicketStore();
  const { queues, fetchQueues } = useSupportSettingsStore();

  // Company-wide ticket visibility (Support › Tickets › View @ Company) = agents,
  // managers, admins, auditors → full support dashboard. Self scope (Employee) →
  // a personal view of only their own tickets. The backend already returns only
  // the employee's own tickets, so this scopes the presentation to match.
  const canSeeAll = useRbacStore((s) => {
    void s.permissions; void s.roles;
    return s.getScope('Support', 'Tickets', 'View') === 'Company';
  });

  useEffect(() => {
    fetchDashboardTickets();
    fetchTickets();
    const interval = setInterval(() => {
      fetchDashboardTickets();
      fetchTickets(undefined, true);
    }, 15000);
    return () => clearInterval(interval);
  }, [fetchDashboardTickets, fetchTickets]);

  // Prefer dashboardTickets, fallback to storeTickets
  const tickets: Ticket[] = useMemo(() => {
    if (Array.isArray(dashboardTickets) && dashboardTickets.length > 0) {
      return dashboardTickets;
    }
    return storeTickets || [];
  }, [dashboardTickets, storeTickets]);

  // Filter open and closed tickets from live application data
  const openTickets = useMemo(() => {
    return tickets.filter(t => !['resolved', 'closed', 'fulfilled'].includes((t.status || '').toLowerCase()));
  }, [tickets]);

  const closedTickets = useMemo(() => {
    return tickets.filter(t => ['resolved', 'closed', 'fulfilled'].includes((t.status || '').toLowerCase()));
  }, [tickets]);

  // Calculate live 24h Average Response and Resolution metrics
  const { avgResponseTime24h, avgResolutionTime24h, responseMet, resolutionMet } = useMemo(() => {
    const now = Date.now();
    const past24hCutoff = now - 24 * 60 * 60 * 1000;

    const parseTime = (dateStr?: string | null): number | null => {
      if (!dateStr) return null;
      const t = new Date(dateStr).getTime();
      return Number.isNaN(t) ? null : t;
    };

    // 1. Avg Response Time in last 24h
    const responseDurations: number[] = [];
    tickets.forEach((t) => {
      const createdTime = parseTime(t.createdAt);
      if (!createdTime) return;

      let respondedTime: number | null =
        parseTime(t.firstRespondedAt) || parseTime(t.sla?.response?.completedAt);

      if (!respondedTime && Array.isArray(t.history) && t.history.length > 0) {
        const responseHistory = t.history.find(
          (h) =>
            (!h.internal && h.actionType) ||
            h.actionType === 'Acknowledgement' ||
            (h.note && (h.note.startsWith('[EMAIL]') || h.actionType))
        );
        if (responseHistory?.createdAt) {
          respondedTime = parseTime(responseHistory.createdAt);
        }
      }

      if (!respondedTime && t.status && !['new', 'open'].includes(t.status.toLowerCase())) {
        const updatedTime = parseTime(t.updatedAt);
        if (updatedTime && updatedTime >= past24hCutoff && updatedTime > createdTime) {
          respondedTime = updatedTime;
        }
      }

      if (respondedTime) {
        if (respondedTime >= past24hCutoff || createdTime >= past24hCutoff) {
          const diff = respondedTime - createdTime;
          if (diff >= 0) {
            responseDurations.push(diff);
          }
        }
      }
    });

    // 2. Avg Resolution Time in last 24h
    const resolutionDurations: number[] = [];
    tickets.forEach((t) => {
      const statusLower = (t.status || '').toLowerCase();
      const isResolved = ['resolved', 'closed', 'fulfilled'].includes(statusLower);
      if (!isResolved) return;

      const createdTime = parseTime(t.createdAt);
      if (!createdTime) return;

      const resolvedTime =
        parseTime(t.resolvedAt) ||
        parseTime(t.closedAt) ||
        parseTime(t.sla?.resolution?.completedAt) ||
        parseTime(t.updatedAt);

      if (resolvedTime && (resolvedTime >= past24hCutoff || createdTime >= past24hCutoff)) {
        const diff = resolvedTime - createdTime;
        if (diff >= 0) {
          resolutionDurations.push(diff);
        }
      }
    });

    let avgRespStr = '—';
    let isRespMet = true;
    if (responseDurations.length > 0) {
      const avgMs = responseDurations.reduce((sum, d) => sum + d, 0) / responseDurations.length;
      avgRespStr = formatDuration(avgMs);
      isRespMet = avgMs / (1000 * 60 * 60) <= 1;
    } else {
      // Fallback: all-time response
      const allResponses: number[] = [];
      tickets.forEach((t) => {
        const cTime = parseTime(t.createdAt);
        const rTime = parseTime(t.firstRespondedAt) || (t.status && !['new', 'open'].includes(t.status.toLowerCase()) ? parseTime(t.updatedAt) : null);
        if (cTime && rTime && rTime > cTime) allResponses.push(rTime - cTime);
      });
      if (allResponses.length > 0) {
        const avgMs = allResponses.reduce((sum, d) => sum + d, 0) / allResponses.length;
        avgRespStr = formatDuration(avgMs);
      }
    }

    let avgResStr = '—';
    let isResMet = true;
    if (resolutionDurations.length > 0) {
      const avgMs = resolutionDurations.reduce((sum, d) => sum + d, 0) / resolutionDurations.length;
      avgResStr = formatDuration(avgMs);
      isResMet = avgMs / (1000 * 60 * 60) <= 12;
    } else {
      // Fallback: all-time resolution
      const allRes: number[] = [];
      tickets.forEach((t) => {
        const isResolved = ['resolved', 'closed', 'fulfilled'].includes((t.status || '').toLowerCase());
        if (!isResolved) return;
        const cTime = parseTime(t.createdAt);
        const resTime = parseTime(t.resolvedAt) || parseTime(t.closedAt) || parseTime(t.updatedAt);
        if (cTime && resTime && resTime > cTime) allRes.push(resTime - cTime);
      });
      if (allRes.length > 0) {
        const avgMs = allRes.reduce((sum, d) => sum + d, 0) / allRes.length;
        avgResStr = formatDuration(avgMs);
      }
    }

    return {
      avgResponseTime24h: avgRespStr,
      avgResolutionTime24h: avgResStr,
      responseMet: isRespMet,
      resolutionMet: isResMet,
    };
  }, [tickets]);

  // Calculate 7-day trends for live metrics
  const trends = useMemo(() => {
    const now = Date.now();
    const sevenDaysAgo = now - 7 * 24 * 60 * 60 * 1000;
    const fourteenDaysAgo = now - 14 * 24 * 60 * 60 * 1000;

    const ticketsLast7 = tickets.filter(t => new Date(t.createdAt).getTime() >= sevenDaysAgo).length;
    const ticketsPrior7 = tickets.filter(t => {
      const time = new Date(t.createdAt).getTime();
      return time >= fourteenDaysAgo && time < sevenDaysAgo;
    }).length;

    let totalTrend = '—';
    let totalDir: 'up' | 'down' | 'none' = 'none';
    if (ticketsPrior7 > 0) {
      const pct = Math.round(((ticketsLast7 - ticketsPrior7) / ticketsPrior7) * 100);
      totalTrend = `${Math.abs(pct)}%`;
      totalDir = pct >= 0 ? 'up' : 'down';
    } else if (ticketsLast7 > 0) {
      totalTrend = `${ticketsLast7} new`;
      totalDir = 'up';
    }

    return { totalTrend, totalDir };
  }, [tickets]);

  // Live Top KPI Cards
  const kpiCards = [
    {
      title: 'Total Ticket',
      value: tickets.length.toLocaleString(),
      trend: tickets.length > 0 ? `${tickets.length}` : '—',
      trendDirection: 'none' as const,
      trendText: 'total tickets in database',
      icon: TicketIcon,
      cardBg: 'bg-blue-50/40 border-blue-200/60 dark:bg-blue-950/20 dark:border-blue-900/40',
      iconBg: 'bg-blue-600 text-white',
    },
    {
      title: 'Open Ticket',
      value: openTickets.length.toLocaleString(),
      trend: openTickets.length > 0 ? `${openTickets.length}` : '—',
      trendDirection: 'none' as const,
      trendText: 'in active backlog',
      icon: Clock,
      cardBg: 'bg-orange-50/40 border-orange-200/60 dark:bg-orange-950/20 dark:border-orange-900/40',
      iconBg: 'bg-orange-500 text-white',
    },
    {
      title: 'Closed Ticket',
      value: closedTickets.length.toLocaleString(),
      trend: closedTickets.length > 0 ? `${closedTickets.length}` : '—',
      trendDirection: 'none' as const,
      trendText: 'resolved tickets',
      icon: CheckCircle2,
      cardBg: 'bg-emerald-50/40 border-emerald-200/60 dark:bg-emerald-950/20 dark:border-emerald-900/40',
      iconBg: 'bg-emerald-600 text-white',
    },
    {
      title: 'Avg Response (Last 24 hr)',
      value: avgResponseTime24h,
      trend: avgResponseTime24h === '—' ? '—' : (responseMet ? '< 1h' : '> 1h'),
      trendDirection: avgResponseTime24h === '—' ? ('none' as const) : (responseMet ? ('down' as const) : ('up' as const)),
      trendText: 'target SLA: 1 hr',
      icon: MessageSquare,
      cardBg: 'bg-purple-50/40 border-purple-200/60 dark:bg-purple-950/20 dark:border-purple-900/40',
      iconBg: 'bg-purple-600 text-white',
    },
    {
      title: 'Avg Resolution (Last 24 hr)',
      value: avgResolutionTime24h,
      trend: avgResolutionTime24h === '—' ? '—' : (resolutionMet ? '< 12h' : '> 12h'),
      trendDirection: avgResolutionTime24h === '—' ? ('none' as const) : (resolutionMet ? ('down' as const) : ('up' as const)),
      trendText: 'target SLA: 12 hr',
      icon: Clock,
      cardBg: 'bg-teal-50/40 border-teal-200/60 dark:bg-teal-950/20 dark:border-teal-900/40',
      iconBg: 'bg-teal-600 text-white',
    },
  ];

  // 1. Live Open Tickets by Team (Bar Chart)
  const openTicketsByTeam = useMemo(() => {
    const teamMap: Record<string, number> = {};
    if (Array.isArray(queues)) {
      queues.forEach((q) => { teamMap[q.label] = 0; });
    }

    openTickets.forEach((t) => {
      const team = t.teamName || (t.teamKey ? (queues.find(q => q.key === t.teamKey)?.label || t.teamKey) : 'Support Team');
      teamMap[team] = (teamMap[team] || 0) + 1;
    });

    const entries = Object.entries(teamMap);
    entries.sort((a, b) => b[1] - a[1]);

    const filtered = entries.filter(([_, count]) => count > 0 || openTickets.length === 0);
    const result = (filtered.length > 0 ? filtered : entries).slice(0, 6);

    return result.map(([team, count], idx) => ({
      team,
      count,
      fill: TEAM_PALETTE[idx % TEAM_PALETTE.length],
    }));
  }, [openTickets, queues]);

  const maxTeamCount = useMemo(() => {
    const counts = openTicketsByTeam.map(t => t.count);
    const maxVal = counts.length > 0 ? Math.max(...counts) : 0;
    return Math.max(10, Math.ceil((maxVal + 2) / 10) * 10);
  }, [openTicketsByTeam]);

  // 2. Live Open Tickets by Type (Donut Chart)
  const openTicketsByType = useMemo(() => {
    const typeMap: Record<string, number> = {};

    openTickets.forEach((t) => {
      const raw = (t.type || '').toLowerCase();
      let label = 'Incident';
      if (raw.includes('inc')) label = 'Incident';
      else if (raw.includes('serv') || raw.includes('req')) label = 'Service Request';
      else if (raw.includes('prob') || raw.includes('bug')) label = 'Problem';
      else if (raw.includes('chan') || raw.includes('cng')) label = 'Change Request';
      else if (raw.includes('acc')) label = 'Access Request';
      else if (t.type) {
        label = t.type.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
      }
      typeMap[label] = (typeMap[label] || 0) + 1;
    });

    const total = openTickets.length || 1;
    const entries = Object.entries(typeMap);
    entries.sort((a, b) => b[1] - a[1]);

    return entries.map(([name, count], idx) => ({
      name,
      count,
      percent: `${Math.round((count / total) * 100)}%`,
      fill: TYPE_PALETTE[name] || TEAM_PALETTE[idx % TEAM_PALETTE.length],
    }));
  }, [openTickets]);

  // 3. Live Open Tickets by Category (Pie Chart)
  const openTicketsByCategory = useMemo(() => {
    const catMap: Record<string, number> = {};
    const dataset = openTickets.length > 0 ? openTickets : tickets;

    dataset.forEach((t) => {
      const cat = t.category || 'General Support';
      catMap[cat] = (catMap[cat] || 0) + 1;
    });

    const total = dataset.length || 1;
    const entries = Object.entries(catMap);
    entries.sort((a, b) => b[1] - a[1]);

    return entries.slice(0, 6).map(([name, count], idx) => {
      const pct = Math.round((count / total) * 100);
      return {
        name,
        count,
        displayPercent: `${pct}%`,
        fill: CAT_PALETTE[idx % CAT_PALETTE.length],
      };
    });
  }, [openTickets, tickets]);

  // 4. Live Service Level Agreement (SLA) (Donut Chart)
  const slaOverview = useMemo(() => {
    let met = 0;
    let atRisk = 0;
    let breached = 0;

    tickets.forEach((t) => {
      const status = (t.status || '').toLowerCase();
      const isResolved = ['resolved', 'closed', 'fulfilled'].includes(status);
      const hasBreached = t.slaBreach && new Date(t.slaBreach).getTime() < Date.now();
      const isBreached = t.slaBreached === true || t.sla?.breached === true || t.slaTracking?.status === 'breached' || (!isResolved && hasBreached);

      if (isBreached) {
        breached++;
      } else if (!isResolved && t.slaBreach && new Date(t.slaBreach).getTime() - Date.now() < 2 * 3600000) {
        atRisk++;
      } else {
        met++;
      }
    });

    const total = tickets.length;
    const metPercent = total === 0 ? '—' : `${Math.round((met / total) * 100)}%`;

    return {
      metPercent,
      hasData: total > 0,
      data: [
        { name: 'Met', count: met, fill: '#10B981' },
        { name: 'At Risk', count: atRisk, fill: '#F59E0B' },
        { name: 'Breached', count: breached, fill: '#EF4444' },
      ],
    };
  }, [tickets]);

  // 5. Live Ticket Status Overview (Donut Chart)
  const statusOverview = useMemo(() => {
    let openCount = 0;
    let inProgressCount = 0;
    let resolvedCount = 0;

    tickets.forEach((t) => {
      const s = (t.status || 'new').toLowerCase();
      if (['resolved', 'closed', 'fulfilled'].includes(s)) {
        resolvedCount++;
      } else if (['in-progress', 'in_progress'].includes(s)) {
        inProgressCount++;
      } else {
        openCount++;
      }
    });

    const total = tickets.length || 1;
    return [
      { name: 'Open', count: openCount, percent: `${Math.round((openCount / total) * 100)}%`, fill: '#F97316' },
      { name: 'In Progress', count: inProgressCount, percent: `${Math.round((inProgressCount / total) * 100)}%`, fill: '#2563EB' },
      { name: 'Resolved', count: resolvedCount, percent: `${Math.round((resolvedCount / total) * 100)}%`, fill: '#10B981' },
    ];
  }, [tickets]);

  // 6. Live Recent Actioned Tickets
  const recentActionedTickets = useMemo(() => {
    return [...tickets]
      .sort((a, b) => {
        const timeA = a.updatedAt ? new Date(a.updatedAt).getTime() : (a.createdAt ? new Date(a.createdAt).getTime() : 0);
        const timeB = b.updatedAt ? new Date(b.updatedAt).getTime() : (b.createdAt ? new Date(b.createdAt).getTime() : 0);
        return timeB - timeA;
      })
      .slice(0, 8);
  }, [tickets]);

  // Dynamic Badge helpers
  const renderTypeBadge = (type?: string) => {
    const t = (type || 'incident').toLowerCase();
    if (t.includes('inc')) {
      return <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold bg-blue-100 text-blue-700 dark:bg-blue-950/60 dark:text-blue-300">Incident</span>;
    }
    if (t.includes('serv') || t.includes('req')) {
      return <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300">Service Request</span>;
    }
    if (t.includes('prob') || t.includes('bug')) {
      return <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold bg-orange-100 text-orange-700 dark:bg-orange-950/60 dark:text-orange-300">Problem</span>;
    }
    if (t.includes('chan') || t.includes('cng')) {
      return <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold bg-purple-100 text-purple-700 dark:bg-purple-950/60 dark:text-purple-300">Change Request</span>;
    }
    return <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 capitalize">{type || 'Ticket'}</span>;
  };

  const renderCategoryBadge = (cat?: string) => {
    const c = (cat || 'General').toLowerCase();
    if (c.includes('hard') || c.includes('equip')) {
      return <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold bg-sky-100 text-sky-700 dark:bg-sky-950/60 dark:text-sky-300">{cat || 'Hardware'}</span>;
    }
    if (c.includes('soft') || c.includes('app')) {
      return <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold bg-cyan-100 text-cyan-700 dark:bg-cyan-950/60 dark:text-cyan-300">{cat || 'Software'}</span>;
    }
    if (c.includes('net') || c.includes('vpn') || c.includes('wifi')) {
      return <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold bg-orange-100 text-orange-700 dark:bg-orange-950/60 dark:text-orange-300">{cat || 'Network'}</span>;
    }
    if (c.includes('mail')) {
      return <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold bg-rose-100 text-rose-700 dark:bg-rose-950/60 dark:text-rose-300">{cat || 'Email'}</span>;
    }
    if (c.includes('acc') || c.includes('auth') || c.includes('ident')) {
      return <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold bg-purple-100 text-purple-700 dark:bg-purple-950/60 dark:text-purple-300">{cat || 'Access & Identity'}</span>;
    }
    return <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300">{cat || 'General'}</span>;
  };

  const renderStatusBadge = (status?: string) => {
    const s = (status || 'new').toLowerCase();
    if (['resolved', 'closed', 'fulfilled'].includes(s)) {
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-semibold bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300 capitalize">{s}</span>;
    }
    if (['in-progress', 'in_progress'].includes(s)) {
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-semibold bg-blue-100 text-blue-700 dark:bg-blue-950/60 dark:text-blue-300 capitalize">In Progress</span>;
    }
    if (['open', 'new'].includes(s)) {
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-semibold bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300 capitalize">{s}</span>;
    }
    return <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-semibold bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 capitalize">{s.replace('-', ' ')}</span>;
  };

  // ---------------------------------------------------------------------------
  // Employee (Self scope) view — only the signed-in person's own tickets.
  // `tickets` is already self-scoped by the backend (requester_uuid = me), so
  // there is nothing company-wide here: no by-team/agent/SLA breakdowns, no
  // approval actions — just "my tickets" and a way to raise a new one.
  // ---------------------------------------------------------------------------
  if (!canSeeAll) {
    const myTickets = [...tickets].sort((a, b) => {
      const tb = b.updatedAt ? new Date(b.updatedAt).getTime() : (b.createdAt ? new Date(b.createdAt).getTime() : 0);
      const ta = a.updatedAt ? new Date(a.updatedAt).getTime() : (a.createdAt ? new Date(a.createdAt).getTime() : 0);
      return tb - ta;
    });
    return (
      <div className="p-4 pb-12 space-y-4 bg-background min-h-full text-foreground text-xs font-sans">
        {error && <p role="alert" className="text-sm text-destructive">{error}</p>}

        {/* Header */}
        <div className="p-4 rounded-xl border border-border bg-card shadow-xs flex flex-col sm:flex-row justify-between sm:items-center gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="size-9 rounded-lg bg-primary/10 border border-primary/20 text-primary flex items-center justify-center shrink-0">
              <TicketIcon className="size-4.5" />
            </div>
            <div className="space-y-0.5 min-w-0">
              <h1 className="tracking-tight text-foreground font-display text-page-title font-semibold truncate">My Support Tickets</h1>
              <p className="text-xs text-muted-foreground truncate max-w-4xl">The tickets and requests you have raised, and their status.</p>
            </div>
          </div>
          <button
            onClick={() => navigate('/support/tickets/new')}
            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 transition-colors shadow-xs shrink-0"
          >
            <TicketIcon className="size-3.5" /> Raise a Ticket
          </button>
        </div>

        {/* Personal KPIs — click to open the matching ticket list */}
        <div className="grid grid-cols-3 gap-4">
          <button
            type="button"
            onClick={() => navigate('/support/tickets')}
            className="text-left rounded-lg border border-border bg-card p-4 shadow-xs flex flex-col justify-between cursor-pointer transition-all hover:border-primary/40 hover:shadow-sm hover:-translate-y-0.5"
          >
            <span className="text-xs font-semibold text-muted-foreground">My Tickets</span>
            <span className="tracking-tight text-foreground font-display text-metric mt-2">{tickets.length}</span>
          </button>
          <button
            type="button"
            onClick={() => navigate('/support/tickets')}
            className="text-left rounded-lg border border-border bg-card p-4 shadow-xs flex flex-col justify-between cursor-pointer transition-all hover:border-primary/40 hover:shadow-sm hover:-translate-y-0.5"
          >
            <span className="text-xs font-semibold text-muted-foreground">Open</span>
            <span className="tracking-tight text-info font-display text-metric mt-2">{openTickets.length}</span>
          </button>
          <button
            type="button"
            onClick={() => navigate('/support/tickets')}
            className="text-left rounded-lg border border-border bg-card p-4 shadow-xs flex flex-col justify-between cursor-pointer transition-all hover:border-primary/40 hover:shadow-sm hover:-translate-y-0.5"
          >
            <span className="text-xs font-semibold text-muted-foreground">Resolved</span>
            <span className="tracking-tight text-success font-display text-metric mt-2">{closedTickets.length}</span>
          </button>
        </div>

        {/* My Tickets table */}
        <div className="rounded-lg border border-border bg-card shadow-xs overflow-hidden">
          <div className="p-4 border-b border-border flex items-center justify-between bg-card/80">
            <h2 className="text-foreground font-display text-card-title">My Tickets</h2>
            <button onClick={() => navigate('/support/tickets')} className="text-xs text-primary hover:underline flex items-center gap-1 font-semibold">
              View all <ArrowRight className="size-3" />
            </button>
          </div>
          <div className="overflow-x-auto">
            {myTickets.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                <TicketIcon className="size-8 opacity-40 mb-2 mx-auto" />
                <p className="text-xs font-semibold">You haven't raised any tickets.</p>
                <p className="text-xs opacity-70 mt-0.5">Use “Raise a Ticket” to report an issue or request something.</p>
              </div>
            ) : (
              <table className="w-full text-left text-xs border-collapse">
                <thead className="border-b border-border bg-muted/30 text-xs font-semibold text-muted-foreground">
                  <tr>
                    <th className="py-2.5 px-4">Ticket</th>
                    <th className="py-2.5 px-4">Summary</th>
                    <th className="py-2.5 px-4">Type</th>
                    <th className="py-2.5 px-4">Status</th>
                    <th className="py-2.5 px-4">Raised</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {myTickets.map((ticket) => (
                    <tr key={ticket.id} className="hover:bg-muted/40 transition-colors cursor-pointer" onClick={() => navigate(`/support/tickets/${ticket.id}`)}>
                      <td className="py-2.5 px-4 font-semibold text-foreground font-mono">{ticket.ticketId || `TB#${String(ticket.id).padStart(5, '0')}`}</td>
                      <td className="py-2.5 px-4 text-muted-foreground font-medium max-w-[240px] truncate" title={ticket.subject}>{ticket.subject || 'No subject'}</td>
                      <td className="py-2.5 px-4">{renderTypeBadge(ticket.type)}</td>
                      <td className="py-2.5 px-4">{renderStatusBadge(ticket.status)}</td>
                      <td className="py-2.5 px-4 text-muted-foreground">{ticket.createdAt ? new Date(ticket.createdAt).toLocaleDateString() : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 pb-12 space-y-4 bg-background min-h-full text-foreground text-xs font-sans">
      {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
      
      {/* ═════════════════════════════════════════════════════════════════════ */}
      {/* TOP HEADER CARD                                                     */}
      {/* ═════════════════════════════════════════════════════════════════════ */}
      <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4 bg-card border border-border p-4 rounded-lg shadow-2xs">
        <div className="space-y-1">
          <div className="flex items-center gap-2.5">
            <div className="size-10 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">
              <LayoutDashboard className="size-5" />
            </div>
            <div>
              <h1 className="font-display text-page-title text-foreground tracking-tight">Support Overview &amp; Dashboard</h1>
              <p className="text-xs text-muted-foreground">
                Executive KPIs · Team Queues · Ticket Distribution · SLA Compliance · Activity Feed
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ═════════════════════════════════════════════════════════════════════ */}
      {/* 1. TOP METRIC CARDS (5-COLUMN KPI STRIP FROM LIVE DATA) */}
      {/* ═════════════════════════════════════════════════════════════════════ */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {kpiCards.map((kpi, idx) => {
          const IconComp = kpi.icon;
          const t = kpi.title.toLowerCase();
          const target =
            (t.includes('avg') || t.includes('response') || t.includes('resolution')) ? '/support/reports' :
            '/support/tickets';
          return (
            <div
              key={idx}
              role="button"
              tabIndex={0}
              onClick={() => navigate(target)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate(target); } }}
              className={cn(
                "rounded-xl border p-4 shadow-2xs flex flex-col justify-between min-h-[110px] transition-all hover:shadow-xs hover:-translate-y-0.5 cursor-pointer",
                kpi.cardBg
              )}
            >
              {/* Line 1: Title & Icon */}
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs sm:text-submenu font-medium text-slate-600 dark:text-slate-400 truncate">
                  {kpi.title}
                </span>
                <div className={cn("size-7 rounded-lg flex items-center justify-center shrink-0 shadow-2xs", kpi.iconBg)}>
                  <IconComp className="size-3.5 stroke-[2.2]" />
                </div>
              </div>

              {/* Line 2: Value */}
              <div className="font-display text-metric text-foreground tracking-tight leading-none my-1">
                {kpi.value}
              </div>

              {/* Line 3: Trend */}
              <div className="flex items-center text-xs font-medium gap-1 text-slate-500 dark:text-slate-400 truncate">
                <span className={cn(
                  "inline-flex items-center font-semibold shrink-0",
                  kpi.trendDirection === 'up' ? "text-emerald-600 dark:text-emerald-400" :
                  kpi.trendDirection === 'down' ? "text-rose-600 dark:text-rose-400" :
                  "text-slate-500 dark:text-slate-400"
                )}>
                  {kpi.trendDirection === 'up' && <ArrowUp className="size-3 mr-0.5 stroke-[2.5]" />}
                  {kpi.trendDirection === 'down' && <ArrowDown className="size-3 mr-0.5 stroke-[2.5]" />}
                  {kpi.trend}
                </span>
                <span className="truncate">
                  {kpi.trendText}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* ═════════════════════════════════════════════════════════════════════ */}
      {/* 2. MIDDLE CHARTS ROW (3-COLUMN EQUAL GRID FROM LIVE DATA) */}
      {/* ═════════════════════════════════════════════════════════════════════ */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* 1. Open Ticket by Team (Bar Chart) */}
        <div className="rounded-2xl border border-slate-200/80 dark:border-border bg-white dark:bg-card p-5 shadow-2xs flex flex-col justify-between min-h-[340px]">
          <div className="flex items-center justify-between pb-2">
            <div className="flex items-center gap-2">
              <Users className="size-4 text-primary" />
              <h2 className="font-display text-card-title text-foreground">
                Open Ticket by Team
              </h2>
            </div>
            <span className="text-xs text-muted-foreground font-mono">
              {openTickets.length} open
            </span>
          </div>

          <div className="h-64 w-full pt-4">
            {openTicketsByTeam.length === 0 || openTickets.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-muted-foreground text-center">
                <Inbox className="size-8 stroke-[1.5] mb-2 opacity-40 text-muted-foreground" />
                <p className="text-xs font-semibold text-foreground">No active team backlog</p>
                <p className="text-xs text-muted-foreground mt-0.5">All tickets are assigned or resolved.</p>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={openTicketsByTeam}
                  margin={{ top: 20, right: 10, left: -15, bottom: 15 }}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" opacity={0.7} />
                  <XAxis
                    dataKey="team"
                    tick={{ fontSize: 10.5, fill: '#64748B' }}
                    axisLine={{ stroke: '#CBD5E1' }}
                    tickLine={false}
                  />
                  <YAxis
                    domain={[0, maxTeamCount]}
                    allowDecimals={false}
                    tick={{ fontSize: 10.5, fill: '#64748B' }}
                    axisLine={{ stroke: '#CBD5E1' }}
                    tickLine={false}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#FFFFFF',
                      borderColor: '#E2E8F0',
                      borderRadius: '8px',
                      fontSize: '11.5px',
                      boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                    }}
                    formatter={(value: any) => [`${value} Tickets`, 'Open']}
                  />
                  <Bar dataKey="count" barSize={34} radius={[4, 4, 0, 0]}>
                    {openTicketsByTeam.map((entry, index) => (
                      <Cell key={`team-bar-${index}`} fill={entry.fill} />
                    ))}
                    <LabelList
                      dataKey="count"
                      position="top"
                      fill="#334155"
                      fontSize={11}
                      fontWeight={600}
                      offset={6}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* 2. Open Ticket by Type (Donut Chart) */}
        <div className="rounded-2xl border border-slate-200/80 dark:border-border bg-white dark:bg-card p-5 shadow-2xs flex flex-col justify-between min-h-[340px]">
          <div className="flex items-center justify-between pb-2">
            <div className="flex items-center gap-2">
              <Tag className="size-4 text-primary" />
              <h2 className="font-display text-card-title text-foreground">
                Open Ticket by Type
              </h2>
            </div>
            <span className="text-xs text-muted-foreground font-mono">
              {openTickets.length} open
            </span>
          </div>

          <div className="h-64 w-full flex items-center justify-between gap-3">
            {openTicketsByType.length === 0 || openTickets.length === 0 ? (
              <div className="w-full h-full flex flex-col items-center justify-center text-muted-foreground text-center">
                <TicketIcon className="size-8 stroke-[1.5] mb-2 opacity-40 text-muted-foreground" />
                <p className="text-xs font-semibold text-foreground">No open tickets by type</p>
                <p className="text-xs text-muted-foreground mt-0.5">Tickets will be categorized here automatically.</p>
              </div>
            ) : (
              <>
                {/* Donut Chart with Center Text */}
                <div className="relative w-[50%] h-full flex items-center justify-center">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={openTicketsByType}
                        cx="50%"
                        cy="50%"
                        innerRadius={56}
                        outerRadius={84}
                        paddingAngle={2}
                        dataKey="count"
                      >
                        {openTicketsByType.map((entry, index) => (
                          <Cell key={`type-pie-${index}`} fill={entry.fill} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{
                          backgroundColor: '#FFFFFF',
                          borderColor: '#E2E8F0',
                          borderRadius: '8px',
                          fontSize: '11px',
                        }}
                        formatter={(value: any, name: any) => [`${value} tickets`, name]}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  {/* Donut Center Label */}
                  <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                    <span className="text-2xl font-semibold text-slate-900 dark:text-white font-display leading-tight">
                      {openTickets.length}
                    </span>
                    <span className="text-xs text-slate-500 dark:text-slate-400 font-medium">
                      Open Tickets
                    </span>
                  </div>
                </div>

                {/* Legend List on Right */}
                <div className="w-[50%] flex flex-col justify-center space-y-2.5 pr-2">
                  {openTicketsByType.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-2 truncate mr-1">
                        <span
                          className="size-2.5 rounded-full shrink-0"
                          style={{ backgroundColor: item.fill }}
                        />
                        <span className="text-slate-700 dark:text-slate-300 font-medium truncate text-xs">
                          {item.name}
                        </span>
                      </div>
                      <span className="font-semibold text-slate-600 dark:text-slate-400 text-xs shrink-0 font-mono">
                        {item.count} ({item.percent})
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* 3. Open Ticket by Category (Pie Chart with internal slice labels) */}
        <div className="rounded-2xl border border-slate-200/80 dark:border-border bg-white dark:bg-card p-5 shadow-2xs flex flex-col justify-between min-h-[340px]">
          <div className="flex items-center justify-between pb-2">
            <div className="flex items-center gap-2">
              <Folder className="size-4 text-primary" />
              <h2 className="font-display text-card-title text-foreground">
                Open Ticket by Category
              </h2>
            </div>
            <span className="text-xs text-muted-foreground font-mono">
              {openTickets.length > 0 ? `${openTickets.length} open` : `${tickets.length} total`}
            </span>
          </div>

          <div className="h-64 w-full flex items-center justify-between gap-3">
            {openTicketsByCategory.length === 0 ? (
              <div className="w-full h-full flex flex-col items-center justify-center text-muted-foreground text-center">
                <Folder className="size-8 stroke-[1.5] mb-2 opacity-40 text-muted-foreground" />
                <p className="text-xs font-semibold text-foreground">No ticket categories recorded</p>
                <p className="text-xs text-muted-foreground mt-0.5">Category distribution will appear here.</p>
              </div>
            ) : (
              <>
                {/* Pie Chart with slice labels */}
                <div className="relative w-[50%] h-full flex items-center justify-center">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={openTicketsByCategory}
                        cx="50%"
                        cy="50%"
                        outerRadius={84}
                        dataKey="count"
                        labelLine={false}
                        label={renderCustomizedPieLabel}
                      >
                        {openTicketsByCategory.map((entry, index) => (
                          <Cell key={`cat-pie-${index}`} fill={entry.fill} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{
                          backgroundColor: '#FFFFFF',
                          borderColor: '#E2E8F0',
                          borderRadius: '8px',
                          fontSize: '11px',
                        }}
                        formatter={(value: any, name: any) => [`${value} tickets`, name]}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                {/* Legend List on Right */}
                <div className="w-[50%] flex flex-col justify-center space-y-2 pr-2">
                  {openTicketsByCategory.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-2 truncate mr-1">
                        <span
                          className="size-2.5 rounded-full shrink-0"
                          style={{ backgroundColor: item.fill }}
                        />
                        <span className="text-slate-700 dark:text-slate-300 font-medium truncate text-xs">
                          {item.name}
                        </span>
                      </div>
                      <span className="font-semibold text-slate-600 dark:text-slate-400 text-xs shrink-0 font-mono">
                        {item.count} ({item.displayPercent})
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

      </div>

      {/* ═════════════════════════════════════════════════════════════════════ */}
      {/* 3. BOTTOM SECTION: RECENT ACTIONED TICKETS (LEFT) + SLA & STATUS (RIGHT) */}
      {/* ═════════════════════════════════════════════════════════════════════ */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">

        {/* LEFT: Recent Actioned Tickets Table (Spans 8 cols) */}
        <div className="lg:col-span-8 rounded-2xl border border-slate-200/80 dark:border-border bg-white dark:bg-card p-5 shadow-2xs flex flex-col justify-between h-full">
          <div className="flex items-center justify-between pb-4">
            <div className="flex items-center gap-2">
              <Clock className="size-4 text-primary" />
              <h2 className="font-display text-card-title text-foreground">
                Recent Actioned Tickets
              </h2>
            </div>
            <span className="text-xs text-muted-foreground font-mono">
              Live updates
            </span>
          </div>

          <div className="overflow-x-auto -mx-5 px-5 flex-1">
            {recentActionedTickets.length === 0 ? (
              <div className="py-12 flex flex-col items-center justify-center text-muted-foreground text-center">
                <TicketIcon className="size-10 stroke-[1.2] mb-2 opacity-40 text-muted-foreground" />
                <p className="text-xs font-semibold text-foreground">No tickets in database</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Create a new ticket from the support team to view real activity here.
                </p>
              </div>
            ) : (
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className="border-b border-slate-100 dark:border-border text-slate-700 dark:text-slate-300 font-semibold text-xs">
                    <th className="pb-3 pr-4 font-semibold flex items-center gap-1 cursor-pointer">
                      Ticket ID <ChevronDown className="size-3 text-slate-400" />
                    </th>
                    <th className="pb-3 px-4 font-semibold">Summary</th>
                    <th className="pb-3 px-4 font-semibold">Type</th>
                    <th className="pb-3 px-4 font-semibold">Category</th>
                    <th className="pb-3 px-4 font-semibold">Team</th>
                    <th className="pb-3 px-4 font-semibold text-center">Status</th>
                    <th className="pb-3 pl-4 font-semibold text-right">Last Updated</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-border/60">
                  {recentActionedTickets.map((ticket) => (
                    <tr
                      key={ticket.id}
                      onClick={() => navigate(`/support/tickets/${ticket.id}`)}
                      className="hover:bg-slate-50/70 dark:hover:bg-muted/40 cursor-pointer transition-colors"
                    >
                      {/* Ticket ID */}
                      <td className="py-3 pr-4 font-semibold text-slate-900 dark:text-white font-mono text-xs">
                        {ticket.ticketId || `TB#${String(ticket.id).padStart(5, '0')}`}
                      </td>

                      {/* Summary */}
                      <td className="py-3 px-4 text-slate-700 dark:text-slate-300 font-medium max-w-[200px] truncate" title={ticket.subject}>
                        {ticket.subject || 'No Subject'}
                      </td>

                      {/* Type badge */}
                      <td className="py-3 px-4">
                        {renderTypeBadge(ticket.type)}
                      </td>

                      {/* Category badge */}
                      <td className="py-3 px-4">
                        {renderCategoryBadge(ticket.category)}
                      </td>

                      {/* Team */}
                      <td className="py-3 px-4 text-slate-600 dark:text-slate-400 font-medium">
                        {ticket.teamName || ticket.teamKey || 'Support Team'}
                      </td>

                      {/* Status badge */}
                      <td className="py-3 px-4 text-center">
                        {renderStatusBadge(ticket.status)}
                      </td>

                      {/* Last Updated */}
                      <td className="py-3 pl-4 text-right text-slate-500 dark:text-slate-400 font-medium font-mono text-xs">
                        {formatTimeAgo(ticket.updatedAt || ticket.createdAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Table Footer */}
          <div className="pt-4 mt-auto border-t border-slate-100 dark:border-border flex items-center justify-between text-xs">
            <span className="text-slate-500 dark:text-slate-400 text-xs">
              Showing {recentActionedTickets.length} of {tickets.length} recent tickets
            </span>
            <button
              onClick={() => navigate('/support/tickets')}
              className="text-primary hover:underline font-semibold text-xs flex items-center gap-1 cursor-pointer transition-colors"
            >
              View all tickets <ArrowRight className="size-3.5" />
            </button>
          </div>
        </div>

        {/* RIGHT: Stack of SLA and Status Overview (Spans 4 cols) */}
        <div className="lg:col-span-4 flex flex-col gap-6">

          {/* 1. Service Level Agreement (SLA) */}
          <div className="rounded-2xl border border-slate-200/80 dark:border-border bg-white dark:bg-card p-5 shadow-2xs flex flex-col justify-between">
            <div className="flex items-center gap-2 pb-2">
              <Shield className="size-4 text-primary" />
              <h2 className="font-display text-card-title text-foreground">
                Service Level Agreement (SLA)
              </h2>
            </div>

            <div className="h-44 w-full flex items-center justify-between gap-3">
              {/* Donut Chart with Dynamic Met % in Center */}
              <div className="relative w-[48%] h-full flex items-center justify-center">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={slaOverview.data}
                      cx="50%"
                      cy="50%"
                      innerRadius={46}
                      outerRadius={66}
                      paddingAngle={2}
                      dataKey="count"
                    >
                      {slaOverview.data.map((entry, index) => (
                        <Cell key={`sla-pie-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#FFFFFF',
                        borderColor: '#E2E8F0',
                        borderRadius: '8px',
                        fontSize: '11px',
                      }}
                      formatter={(val: any, name: any) => [`${val} tickets`, name]}
                    />
                  </PieChart>
                </ResponsiveContainer>
                {/* Center text */}
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                  <span className="text-xl font-semibold text-slate-900 dark:text-white font-display leading-tight">
                    {slaOverview.metPercent}
                  </span>
                  <span className="text-xs text-slate-500 dark:text-slate-400 font-medium">
                    SLA Met
                  </span>
                </div>
              </div>

              {/* Legend list on Right */}
              <div className="w-[52%] flex flex-col justify-center space-y-3 pr-2">
                {slaOverview.data.map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <span
                        className="size-2 rounded-full shrink-0"
                        style={{ backgroundColor: item.fill }}
                      />
                      <span className="text-slate-700 dark:text-slate-300 font-medium text-xs">
                        {item.name}
                      </span>
                    </div>
                    <span className="font-semibold text-slate-900 dark:text-white text-xs font-mono">
                      {item.count}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* 2. Ticket Status Overview */}
          <div className="rounded-2xl border border-slate-200/80 dark:border-border bg-white dark:bg-card p-5 shadow-2xs flex flex-col justify-between">
            <div className="flex items-center gap-2 pb-2">
              <BarChart2 className="size-4 text-primary" />
              <h2 className="font-display text-card-title text-foreground">
                Ticket Status Overview
              </h2>
            </div>

            <div className="h-44 w-full flex items-center justify-between gap-3">
              {/* Donut Chart with Dynamic Total in Center */}
              <div className="relative w-[48%] h-full flex items-center justify-center">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={statusOverview}
                      cx="50%"
                      cy="50%"
                      innerRadius={46}
                      outerRadius={66}
                      paddingAngle={2}
                      dataKey="count"
                    >
                      {statusOverview.map((entry, index) => (
                        <Cell key={`status-pie-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: '#FFFFFF',
                        borderColor: '#E2E8F0',
                        borderRadius: '8px',
                        fontSize: '11px',
                      }}
                      formatter={(val: any, name: any) => [`${val} tickets`, name]}
                    />
                  </PieChart>
                </ResponsiveContainer>
                {/* Center text */}
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                  <span className="text-xl font-semibold text-slate-900 dark:text-white font-display leading-tight">
                    {tickets.length.toLocaleString()}
                  </span>
                  <span className="text-xs text-slate-500 dark:text-slate-400 font-medium">
                    Total Tickets
                  </span>
                </div>
              </div>

              {/* Legend list on Right */}
              <div className="w-[52%] flex flex-col justify-center space-y-3 pr-2">
                {statusOverview.map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <span
                        className="size-2 rounded-full shrink-0"
                        style={{ backgroundColor: item.fill }}
                      />
                      <span className="text-slate-700 dark:text-slate-300 font-medium text-xs">
                        {item.name}
                      </span>
                    </div>
                    <span className="font-semibold text-slate-900 dark:text-white text-xs font-mono">
                      {item.count} ({item.percent})
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

        </div>

      </div>

      {/* BOTTOM SPACING BUFFER */}
      <div className="h-20 w-full shrink-0" aria-hidden="true" />

    </div>
  );
}
